import type { Dtos, SuretyAccountId } from "@inrev/common";
import {
	CreateAccountIndemnityAgreementDto,
	useGetAccountIndemnityAgreementFileUrl,
} from "@portal/api/account/agent";
import { FormItemLabel } from "@portal/components/form";
import { FormField } from "@portal/components/form/FormField";
import { FormFieldGroup } from "@portal/components/form/FormFieldGroup";
import { FormInput } from "@portal/components/form/fields";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Modal } from "@portal/components/layout/Modal";
import { Button, Icon, Text } from "@portal/components/primitives";
import { type RefObject, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
	IndemnityFormData,
	InProgressIndemnityAgreement,
} from "../../../../../../../account/types";
import { ElectronicToPhysicalModal } from "../components/ElectronicToPhysicalModal";
import { IndemnityAgreementStatusIndicator } from "../components/IndemnityAgreementStatusIndicator";
import { IndemnityAgreementSectionLayout } from "../layout/IndemnityAgreementLayout";

export type ElectronicIndemnityAgreementSectionProps = {
	accountId: SuretyAccountId;
	indemnityAgreement?: InProgressIndemnityAgreement;
	indemnitors: Dtos.SuretyAccount.IndemnityAgreement.Get.Response["indemnitors"];
	formMethods: ReturnType<
		typeof useForm<IndemnityFormData, unknown, CreateAccountIndemnityAgreementDto>
	>;
	isIncomplete: boolean;
	isCreating: boolean;
	signatureMethodComponent: React.ReactNode;
	indemnitorListComponent: React.ReactNode;
	domReadyRef: RefObject<HTMLDivElement | null>;
	onSubmit: (data: CreateAccountIndemnityAgreementDto) => void;
	onOpenElectronicToPhysical: () => void;
	electronicToPhysicalOpen: boolean;
	onCloseElectronicToPhysical: () => void;
};

export const ElectronicIndemnityAgreementSection = ({
	accountId,
	indemnityAgreement,
	indemnitors,
	formMethods,
	isIncomplete,
	isCreating,
	signatureMethodComponent,
	indemnitorListComponent,
	domReadyRef,
	onSubmit,
	onOpenElectronicToPhysical,
	electronicToPhysicalOpen,
	onCloseElectronicToPhysical,
}: ElectronicIndemnityAgreementSectionProps) => {
	const { downloadIndemnityAgreement, indemnityAgreementFileUrlLoading } =
		useGetAccountIndemnityAgreementFileUrl({
			accountId,
			indemnityAgreementId: indemnityAgreement?.id,
			asDownload: true,
		});

	const flatIndemnitors = useMemo(
		() => [
			...indemnitors.companies.map((c) => ({ ...c, type: "company" as const })),
			...indemnitors.individuals.map((i) => ({ ...i, type: "individual" as const })),
		],
		[indemnitors],
	);

	const bodyFormComponent = isIncomplete ? (
		<div className="flex flex-col space-y-[36px] py-[10px] pb-[20px]">
			<div className="flex flex-col space-y-[15px] w-full h-fit min-h-fit">
				{indemnitors.companies.map((company, index) => (
					<FormFieldGroup key={company.id} className="py-[8px] rounded-sm space-y-[10px]">
						<div className="flex items-center space-x-[10px] font-semibold">
							<Text type="caption" color="tertiary">
								{index + 1}.
							</Text>
							<Text type="body-strong" color="primary">
								{company.name}
							</Text>
						</div>
						<FormField condensed className="px-[18px]">
							<FormItemLabel condensed required>
								Signer Email
							</FormItemLabel>
							<FormInput
								control={formMethods.control}
								name={`companySigners.${index}.signerEmail`}
								className="bg-surface-base"
								compact
							/>
						</FormField>
					</FormFieldGroup>
				))}
				{indemnitors.individuals.map((individual, index) => (
					<FormFieldGroup key={individual.id} className="py-[8px] rounded-sm space-y-[10px]">
						<div className="flex items-center space-x-[10px] font-semibold">
							<Text type="caption" color="tertiary">
								{indemnitors.companies.length + index + 1}.
							</Text>
							<Text type="body-strong" color="primary">
								{individual.name}
							</Text>
						</div>
						<FormField condensed className="px-[18px]">
							<FormItemLabel condensed required>
								Email
							</FormItemLabel>
							<FormInput
								control={formMethods.control}
								name={`individualSigners.${index}.signerEmail`}
								className="bg-surface-base"
								compact
							/>
						</FormField>
					</FormFieldGroup>
				))}
			</div>
			<ConfirmationModalClickWrapper
				message="Are you sure you want to send this indemnity agreement for e-signatures?"
				onConfirm={() => formMethods.handleSubmit(onSubmit)()}
				className="max-w-[500px] self-center"
			>
				<Button
					color="primary"
					className="self-center px-[50px]"
					leadingIcon={<Icon type="envelope" size="small" color="inverse" />}
				>
					Send Indemnity Agreement For E-Signature
				</Button>
			</ConfirmationModalClickWrapper>
		</div>
	) : (
		<div className="flex flex-col space-y-5">
			<Text type="title" color="primary">
				Next Steps
			</Text>
			<ul className="flex flex-col space-y-4">
				<li>
					<Text type="body" color="secondary">
						Instruct each signer to check their email inbox for an email with a link to
						electronically sign the indemnity agreement.
					</Text>
				</li>
				<li>
					<Text type="body" color="secondary">
						If a physical copy of the indemnity agreement is required instead, you can{" "}
						<button
							type="button"
							className="cursor-pointer underline text-link hover:text-link-hover"
							onClick={() => downloadIndemnityAgreement()}
						>
							download
						</button>{" "}
						a copy of the indemnity agreement and{" "}
						<button
							type="button"
							className="cursor-pointer underline text-link hover:text-link-hover"
							onClick={onOpenElectronicToPhysical}
						>
							upload a scanned copy
						</button>{" "}
						of the completed agreement.
					</Text>
				</li>
				<li>
					<Text type="body" color="secondary">
						Wait for all e-signatures to be completed. You can follow the signing status next to
						each signer above. When all signers have finished signing, this page will automatically
						update to show a "Complete" status.
					</Text>
				</li>
			</ul>
			{electronicToPhysicalOpen && indemnityAgreement && (
				<Modal onClickOutside={onCloseElectronicToPhysical}>
					<ElectronicToPhysicalModal
						domReadyRef={domReadyRef}
						indemnityAgreement={indemnityAgreement}
						suretyAccountId={accountId}
					/>
				</Modal>
			)}
		</div>
	);

	return (
		<IndemnityAgreementSectionLayout
			status={
				<IndemnityAgreementStatusIndicator
					status={isIncomplete ? "incomplete" : (indemnityAgreement?.status ?? "pending")}
					signatureMethod={isIncomplete ? null : (indemnityAgreement?.signatureMethod ?? null)}
					indemnitorsCompleted={flatIndemnitors.filter((i) => i.completed).length}
					indemnitorsTotal={flatIndemnitors.length}
				/>
			}
			signatureMethod={signatureMethodComponent}
			requiredIndemnitors={indemnitorListComponent}
			body={bodyFormComponent}
			bodyTitle="Indemnity Agreement"
			bodyTestId="agent-account-indemnity-agreement-card"
			domReadyRef={domReadyRef}
			loading={isCreating || indemnityAgreementFileUrlLoading}
		/>
	);
};
